import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnDestroy, Optional, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DurationHmsPipe } from 'src/app/pipes/duration-hms/duration-hms.pipe';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { DailySummary } from 'src/models';
import { TimetubeContext } from './context';


type PartialDailySummary = Partial<DailySummary>;

@Component({
  selector: 'app-daily-timetube',
  templateUrl: './daily-timetube.component.html',
  styleUrls: ['./daily-timetube.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailyTimetubeComponent implements OnDestroy {
  @HostBinding('class.single-color')
  @Input() singleColor: boolean;
  @Input() singleColorValue: string;
  @Input() timelineReport = false;
  @Input() includeUnpaidBreaks = true;
  @Input() hideTooltip = false;
  @Input() showMeetingTime = false;
  @Output() beforeContextCheck = new EventEmitter<TimetubeContext>();
  unpaidLeavesEnabled: boolean;

  private _useContext: boolean;
  @Input() set useContext(value: boolean) {
    if (value === this._useContext) return;
    this._useContext = value;
    if (this.ctx) {
      this.beforeContextCheck.emit(this.ctx);
      this.ctx.check();
    }
  }
  get useContext() { return this._useContext; }

  private _summary: PartialDailySummary;
  get summary() { return this._summary; }
  @Input() set summary(value: PartialDailySummary) {
    this._summary = value;
    if (this.ctx) {
      this.beforeContextCheck.emit(this.ctx);
      this.ctx.check();
    }
  }

  get totalFieldName() {
    return this.ctx?.fieldName || 'total';
  }

  get fullSeconds() {
    if (this.useContext && this.ctx && this.ctx.maxVal) return this.ctx.maxVal;
    return 60 * 60 * 12;
  }

  @HostBinding('class.disabled') get disabled() {
    return !this.summary || !this.fullSeconds;
  }

  private cleanup: () => void;

  constructor(
    @Optional() public ctx: TimetubeContext,
    private cd: ChangeDetectorRef,
    private growthBook: GrowthBookService,
    private durationHms: DurationHmsPipe,
    private translate: TranslateService,
  ) {
    this.cleanup = ctx?.register(this);
    this.unpaidLeavesEnabled = this.growthBook.getFeatureValue<boolean>('unpaid-leaves', false);
  }

  ngOnDestroy() {
    if (this.cleanup) this.cleanup();
  }

  rerender() {
    this.cd.markForCheck();
  }

  get totalSeconds() {
      return ((this.includeUnpaidBreaks ? (this.summary?.unpaidBreak || 0) + (this.summary?.unpaidLeave || 0) : 0) + this.fullSeconds);
  }

  get totalWidth() {
    return (this.summary?.[this.totalFieldName] || 0) / this.totalSeconds;
  }

  get meetingSeconds() {
    return this.showMeetingTime ? (this.summary?.meeting || 0) : 0;
  }

  get computerSeconds() {
    return Math.max(0, (this.summary?.computer || 0) - this.meetingSeconds);
  }

  get computerWidth() {
    return this.computerSeconds / this.totalSeconds;
  }

  get meetingWidth() {
    return this.meetingSeconds / this.totalSeconds;
  }

  get manualWidth() {
    return (this.summary?.manual || 0) / this.totalSeconds;
  }

  get mobileWidth() {
    return (this.summary?.mobile || 0) / this.totalSeconds;
  }

  get paidBreakWidth() {
      return (this.summary?.paidBreak || 0) / this.totalSeconds;
  }

  get unpaidBreakWidth() {
      return (this.summary?.unpaidBreak || 0) / this.totalSeconds;
  }

  get paidLeaveWidth() {
    return (this.summary?.paidLeave || 0) / this.totalSeconds;
  }

  get unpaidLeaveWidth() {
    return (this.summary?.unpaidLeave || 0) / this.totalSeconds;
  }

  get paidLeaveTooltip(): string {
    const duration = this.durationHms.transform(this.summary?.paidLeave || 0);
    const label = this.translate.instant('editTime.paidLeave');
    return this.timelineReport && this.unpaidLeavesEnabled
      ? `${label} - ${duration}`
      : duration;
  }

  get unpaidLeaveTooltip(): string {
    const duration = this.durationHms.transform(this.summary?.unpaidLeave || 0);
    const label = this.translate.instant('editTime.unpaidLeave');
    return `${label} - ${duration}`;
  }

  get breakTooltip(): string {
    const paidBreak = this.durationHms.transform(this.summary?.paidBreak || 0);
    const unpaidBreak = this.durationHms.transform(this.summary?.unpaidBreak || 0);
    const paidBreakLabel = this.translate.instant('editTime.paidBreak');
    const unpaidBreakLabel = this.translate.instant('editTime.unpaidBreak');
    return `${paidBreakLabel}: ${paidBreak}\n${unpaidBreakLabel}: ${unpaidBreak}`;
  }

}
